/**
 * The single batching boundary for store notifications (review S-5).
 *
 * The Command Bus opens a batch around every `execute` and `transaction`.
 * Stores publish new snapshots immediately (so the engine always reads current
 * state), but their *notifications* are deferred to the moment the outermost
 * batch closes. React therefore sees one consistent update per command, never
 * an intermediate state halfway through a transaction.
 *
 * Outside a batch, scheduled work runs immediately.
 */
export class ChangeScheduler {
  private depth = 0;
  private readonly pending = new Set<() => void>();

  get inBatch(): boolean {
    return this.depth > 0;
  }

  /** Runs `fn` inside a batch; nested batches flatten into the outermost one. */
  batch<T>(fn: () => T): T {
    this.depth++;
    try {
      return fn();
    } finally {
      this.depth--;
      if (this.depth === 0) this.flush();
    }
  }

  /**
   * Runs `work` now, or once when the outermost batch closes.
   * The same function scheduled twice in one batch runs once.
   */
  schedule(work: () => void): void {
    if (this.depth === 0) {
      work();
      return;
    }
    this.pending.add(work);
  }

  /** Drains pending work. Work scheduled while flushing is drained in the same pass. */
  private flush(): void {
    while (this.pending.size > 0) {
      const batch = [...this.pending];
      this.pending.clear();
      for (const work of batch) work();
    }
  }
}
